import React, { useState, useEffect } from 'react';
import { Box, IconButton, Popover, Grid, useTheme, useMediaQuery, InputAdornment } from '@mui/material';
import { CalendarMonth, ChevronLeft, ChevronRight, KeyboardArrowDown } from '@mui/icons-material';
import Text from 'components/mui/Text';
import Button from 'components/mui/Button';
import AppTextFieldComponent from 'components/mui/AppTextFieldComponent';
import { typography } from 'theme';
import { alpha } from '@mui/material/styles';
import { formatDate } from 'utils/date.utils';

const DatePicker = ({ value, onChange, label = "SIP Date", placeholder, startIcon, endIcon, selectSx = {}, minDate, maxDate, shouldDisableDate, error = false, required = false, helperText, displayPrefix = "", displayFormat = "DD/MM/YYYY", showEndIcon = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [inputValue, setInputValue] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [yearRangeStart, setYearRangeStart] = useState(() => {
    const currentYear = new Date().getFullYear();
    return Math.floor(currentYear / 10) * 10; // Start of decade
  });

  const open = Boolean(anchorEl);

  // Sync selectedDate and inputValue with value prop
  useEffect(() => {
    if (value) {
      const dateValue = new Date(value);
      if (!isNaN(dateValue.getTime())) {
        setSelectedDate(dateValue);
        setCurrentDate(dateValue);
        setInputValue(formatDateDisplay(dateValue));
        setHasInteracted(true);
      } else {
        setInputValue('');
        setHasInteracted(false);
      }
    } else {
      setInputValue('');
      // Only reset hasInteracted if value is explicitly cleared (not on initial mount)
      if (hasInteracted) {
        // Keep hasInteracted true so placeholder shows dd/mm/yyyy after erasing
      }
    }
  }, [value]);

  const handleClick = (event) => {
    setHasInteracted(true);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setViewMode('month'); // Reset to month view when closing
  };

  const formatDateDisplay = (date) => {
    if (!date) return '';
    const formattedDate = formatDate(date, displayFormat);
    return displayPrefix ? `${displayPrefix} ${formattedDate}` : formattedDate;
  };

  // Parse date string from dd/mm/yyyy format
  const parseDateInput = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    // Validate the date
    if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
      return date;
    }
    return null;
  };

  const handleInputChange = (e) => {
    setHasInteracted(true);
    let newValue = e.target.value;

    // Remove all non-numeric characters except slashes
    newValue = newValue.replace(/[^\d/]/g, '');

    // Auto-format as user types (dd/mm/yyyy) only if using default format
    if (displayFormat === 'DD/MM/YYYY' && newValue.length > 0) {
      const digits = newValue.replace(/\//g, '');
      let formatted = '';

      for (let i = 0; i < digits.length && i < 8; i++) {
        if (i === 2 || i === 4) {
          formatted += '/';
        }
        formatted += digits[i];
      }

      newValue = formatted;
    }

    setInputValue(newValue);

    // If input is completely cleared, clear the form value
    if (newValue.length === 0) {
      onChange({ target: { value: '' } });
      return;
    }

    // Only attempt dd/mm/yyyy parsing if we're using that format
    if (displayFormat === 'DD/MM/YYYY' && newValue.length === 10) {
      const parsedDate = parseDateInput(newValue);
      if (parsedDate) {
        const isoDate = formatDate(parsedDate, 'YYYY-MM-DD');
        onChange({ target: { value: isoDate } });
        setSelectedDate(parsedDate);
        setCurrentDate(parsedDate);
      } else {
        onChange({ target: { value: '' } });
      }
    }
  };

  const handleInputBlur = () => {
    // If input is empty, clear the form value
    if (!inputValue || inputValue.length === 0) {
      onChange({ target: { value: '' } });
      setInputValue('');
      return;
    }

    // If the input matches our formatted selected date, it's valid, don't clear it
    if (inputValue === formatDateDisplay(selectedDate)) {
      return;
    }

    // If it's the standard format, we can try to parse it
    if (displayFormat === 'DD/MM/YYYY') {
      if (inputValue.length < 10 || !parseDateInput(inputValue)) {
        onChange({ target: { value: '' } });
        setInputValue('');
      }
    } else {
      // For other formats, if it doesn't match selectedDate formatted, it might be an manual edit
      // but we don't have a reliable parser for custom formats, so we revert to selected
      setInputValue(formatDateDisplay(selectedDate));
    }
  };

  const isDateDisabled = (dateToCheck) => {
    const check = new Date(dateToCheck);
    check.setHours(0, 0, 0, 0);

    // Check custom shouldDisableDate first
    if (shouldDisableDate && shouldDisableDate(dateToCheck)) {
      return true;
    }

    // Check minDate
    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      if (check < min) return true;
    }

    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      if (check > max) return true;
    }

    return false;
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    if (isDateDisabled(newDate)) return;
    setSelectedDate(newDate);
    // Return ISO format (YYYY-MM-DD) for form compatibility, but display as dd/mm/yyyy
    const isoDate = formatDate(newDate, 'YYYY-MM-DD');
    setInputValue(formatDateDisplay(newDate));
    onChange({ target: { value: isoDate } });
    handleClose();
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const navigateYear = (direction) => {
    if (direction === 'prev') {
      setYearRangeStart(prev => prev - 10);
    } else {
      setYearRangeStart(prev => prev + 10);
    }
  };

  const handleYearSelect = (year) => {
    if (minDate && year < new Date(minDate).getFullYear()) return;
    if (maxDate && year > new Date(maxDate).getFullYear()) return;
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setFullYear(year);
      return newDate;
    });
    setViewMode('month');
  };

  const handleHeaderClick = () => {
    setViewMode(viewMode === 'month' ? 'year' : 'month');
  };

  const getYearRange = () => {
    const years = [];
    for (let i = 0; i < 12; i++) {
      years.push(yearRangeStart + i);
    }
    return years;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        isPreviousMonth: true
      });
    }
    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day: day,
        isCurrentMonth: true,
        isPreviousMonth: false
      });
    }
    // Add next month's leading days to complete the week
    const currentLength = days.length;
    const remainingCells = currentLength % 7 === 0 ? 0 : 7 - (currentLength % 7);
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day: day,
        isCurrentMonth: false,
        isPreviousMonth: false
      });
    }
    return days;
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const monthDays = getDaysInMonth(currentDate);
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  return (
    <Box sx={{ minWidth: 60 }}>
      {/* Editable TextField */}
      <AppTextFieldComponent
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onClick={handleClick}
        placeholder={placeholder || (inputValue && inputValue.length > 0 ? 'dd/mm/yyyy' : 'Select Date of Birth')}
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">
              {startIcon}
            </InputAdornment>
          ),
          endAdornment: showEndIcon && (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClick}
                edge="end"
                sx={{
                  color: theme.palette.custom?.text?.subheading || '#94a3b8',
                  padding: '4px',
                  transition: 'transform 0.2s ease-in-out',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                {endIcon ? endIcon : <CalendarMonth sx={{ fontSize: { xs: '16px', md: '20px' } }} />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          maxLength: 10,
          style: {
            textAlign: 'left',
            cursor: 'text'
          }
        }}
        sx={{
          width: selectSx.width || '100%',
          maxWidth: selectSx.maxWidth || 'none',
          '& .MuiOutlinedInput-root': {
            backgroundColor: selectSx.backgroundColor || (theme.palette.custom?.background?.layer2 || '#2A2D3A'),
            height: selectSx.height || '56px',
            minHeight: selectSx.minHeight || '56px',
            borderRadius: selectSx.borderRadius,
            cursor: 'text',
            boxShadow: 'none',
            border: 'none',
            '& fieldset': {
              borderColor: (error === true) ? '#d32f2f' : '#3F4252',
              borderWidth: '1px'
            },
            '&:hover fieldset': {
              borderColor: (error === true) ? '#d32f2f' : '#1455FF'
            },
            '&.Mui-focused fieldset': {
              borderColor: (error === true) ? '#d32f2f' : '#1455FF'
            }
          },
          '& .MuiInputLabel-root': {
            color: theme.palette.custom?.text?.label || 'white',
            fontSize: '14px',
            fontWeight: 400,
            transform: 'translate(14px, -9px) scale(0.75)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)'
            },
            '& .MuiInputLabel-asterisk': {
              color: 'red'
            }
          },
          '& .MuiInputBase-input': {
            color: selectSx.color || (theme.palette.custom?.text?.body || 'white'),
            fontSize: '14px'
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#808080',
            opacity: 1
          }
        }}
      />

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: theme.palette.custom?.background?.helper || '#1e2a3a',
            color: theme.palette.custom?.text?.heading,
            borderRadius: theme.spacing(1.5),
            border: `1px solid ${theme.palette.custom?.stroke?.divider || '#334155'}`,
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            minWidth: { xs: 250, md: 300 },
            maxWidth: { xs: 270, md: 320 },
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ p: { xs: theme.spacing(1), md: theme.spacing(2) } }}>
          {/* Header with month/year navigation */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: theme.spacing(1.5), md: theme.spacing(2) },
            px: { xs: theme.spacing(0.5), md: theme.spacing(1) }
          }}>
            <IconButton
              onClick={() => viewMode === 'month' ? navigateMonth('prev') : navigateYear('prev')}
              sx={{
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                p: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                '&:hover': { backgroundColor: alpha(theme.palette.custom?.text?.heading || '#FFFFFF', 0.1) },
                '& svg': {
                  fontSize: { xs: '18px', md: '24px' },
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Text
              onClick={handleHeaderClick}
              sx={{
                fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.paragraph },
                fontWeight: typography.fontWeight.semibold,
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              {viewMode === 'month' ? getMonthName(currentDate) : `${yearRangeStart} - ${yearRangeStart + 11}`}
            </Text>
            <IconButton
              onClick={() => viewMode === 'month' ? navigateMonth('next') : navigateYear('next')}
              sx={{
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                p: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                '&:hover': { backgroundColor: alpha(theme.palette.custom?.text?.heading || '#FFFFFF', 0.1) },
                '& svg': {
                  fontSize: { xs: '18px', md: '24px' },
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {viewMode === 'month' ? (
            <>
              {/* Day labels */}
              <Grid container spacing={0} sx={{ mb: { xs: theme.spacing(0.75), md: theme.spacing(1) } }}>
                {days.map((day, index) => (
                  <Grid item xs={12 / 7} key={index}>
                    <Box sx={{
                      textAlign: 'center',
                      py: { xs: theme.spacing(0.5), md: theme.spacing(0.8) },
                      fontSize: { xs: typography.fontSize.helperText, md: '10px' },
                      fontWeight: typography.fontWeight.medium,
                      color: theme.palette.custom?.text?.subheading || '#94a3b8',
                      textTransform: 'uppercase'
                    }}>
                      {day}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar grid */}
              <Grid container spacing={0}>
                {monthDays.map((dayObj, index) => {
                  const { day, isCurrentMonth, isPreviousMonth } = dayObj;
                  const dateToCheck = new Date(currentYear, currentMonth, day);
                  const isDisabled = isCurrentMonth && isDateDisabled(dateToCheck);

                  const isSelected = selectedDate.getDate() === day &&
                    selectedDate.getMonth() === currentDate.getMonth() &&
                    selectedDate.getFullYear() === currentDate.getFullYear() &&
                    isCurrentMonth;

                  return (
                    <Grid item xs={12 / 7} key={index}>
                      <Box
                        onClick={() => day && isCurrentMonth && !isDisabled && handleDateSelect(day)}
                        sx={{
                          textAlign: 'center',
                          py: { xs: theme.spacing(0.5), md: theme.spacing(1) },
                          cursor: (day && isCurrentMonth && !isDisabled) ? 'pointer' : 'default',
                          fontSize: { xs: typography.fontSize.helperText, md: '12px' },
                          fontWeight: typography.fontWeight.medium,
                          color: isDisabled
                            ? alpha(theme.palette.custom?.text?.subheading || '#64748b', 0.3)
                            : isCurrentMonth
                              ? (isSelected
                                ? theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF'
                                : theme.palette.custom?.text?.heading || '#FFFFFF')
                              : theme.palette.custom?.text?.subheading || '#64748b',
                          backgroundColor: isSelected && !isDisabled
                            ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                            : 'transparent',
                          borderRadius: '50%',
                          minHeight: { xs: 24, md: 28 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: { xs: theme.spacing(0.1), md: theme.spacing(0.2) },
                          '&:hover': (day && isCurrentMonth && !isDisabled) ? {
                            backgroundColor: isSelected
                              ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                              : alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.2),
                          } : {},
                        }}
                      >
                        {day}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <>
              {/* Year grid */}
              <Grid container spacing={0}>
                {getYearRange().map((year, index) => {
                  const isSelected = currentDate.getFullYear() === year;
                  const isDisabled = (minDate && year < new Date(minDate).getFullYear()) || (maxDate && year > new Date(maxDate).getFullYear());

                  return (
                    <Grid item xs={4} key={index}>
                      <Box
                        onClick={() => !isDisabled && handleYearSelect(year)}
                        sx={{
                          textAlign: 'center',
                          py: { xs: theme.spacing(1), md: theme.spacing(1.5) },
                          cursor: isDisabled ? 'default' : 'pointer',
                          fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
                          fontWeight: typography.fontWeight.medium,
                          opacity: isDisabled ? 0.3 : 1,
                          color: isSelected && !isDisabled
                            ? theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF'
                            : theme.palette.custom?.text?.heading || '#FFFFFF',
                          backgroundColor: isSelected && !isDisabled
                            ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                            : 'transparent',
                          borderRadius: theme.spacing(1),
                          mx: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                          mb: { xs: theme.spacing(0.5), md: theme.spacing(1) },
                          '&:hover': {
                            backgroundColor: (!isDisabled && (isSelected
                              ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                              : alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.2))),
                          },
                        }}
                      >
                        {year}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Continue button */}
          <Box sx={{ mt: { xs: theme.spacing(1.5), md: theme.spacing(2) }, textAlign: 'center' }}>
            <Button
              onClick={handleClose}
              sx={{
                backgroundColor: theme.palette.custom?.button?.primary || '#0ea5e9',
                color: theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF',
                padding: { xs: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`, md: `${theme.spacing(1)} ${theme.spacing(2.5)}` },
                fontSize: { xs: typography.fontSize.helperText, md: '12px' },
                fontWeight: typography.fontWeight.semibold,
                borderRadius: theme.spacing(1),
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.custom?.button?.primary || '#0284c7',
                  opacity: 0.9,
                },
                minWidth: { xs: 60, md: 80 },
                boxShadow: 'none',
                width: '100%',
              }}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DatePicker;
